import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import {
  Box,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CloseOutlined, LockOutlined } from '@ant-design/icons';

import { DataGrid } from '../../../components/grid/DataGrid';
import FormCard from '../../../components/FormCard';
import Button from '../../../components/Button';
import InputLabel from '../../../components/form/InputLabel';
import OutlinedInput from '../../../components/form/OutlinedInput';
import Autocomplete from '../../../components/form/Autocomplete';
import DateTextField from '../../../components/form/DateTextField';

import {
  alterarFiltros,
  alterarPaginacao,
  alterarSort,
  fecharModal,
  selecionarLog
} from '../../../reducers/logsIntegracao.reducer';
import {
  FLUXO_DOMAIN,
  TIPO_EVENTO_DOMAIN,
  SITUACAO_DOMAIN,
  buildServicoLabel,
  formatDateTime,
  formatDuracao,
  isEncryptedFormat,
  parseBrDate
} from '../../../utils/logAuditoria';
import LogSituacaoBadge from '../../configuracoes/LogsIntegracao/components/LogSituacaoBadge';
import LogDetalhe from '../../configuracoes/LogsIntegracao/components/LogDetalhe';

// ============================================================================
// Modal de auditoria escopado a uma simulacao.
// Abre a partir do botao "Logs de Integracao" na lista de simulacoes da
// oportunidade (ver Simulacoes.js). UI compartilha componentes com a tela
// global em pages/configuracoes/LogsIntegracao.
// ============================================================================

const LogsIntegracaoModal = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { modalAberto, lista, sort, detalhe } = useSelector(
    (state) => state.logsIntegracao
  );

  const formProps = {
    ...useForm(),
    rules: {},
    initialValues: {
      dataInicioFrom: '',
      dataInicioTo: '',
      situacao: null,
      codigoFluxo: null,
      tipoEvento: null
    }
  };

  const handleClose = () => {
    dispatch(fecharModal());
  };

  const onChangeData = (campo) => (valorBr) => {
    dispatch(alterarFiltros({ [campo]: parseBrDate(valorBr) }));
  };

  const onChangeSituacao = (item) => {
    if (!item) return dispatch(alterarFiltros({ flagSuccesso: null }));
    dispatch(alterarFiltros({ flagSuccesso: item.id === 'sucesso' }));
  };

  const onChangeFluxo = (item) => {
    dispatch(alterarFiltros({ codigoFluxo: item ? [item.id] : [] }));
  };

  const onChangeTipoEvento = (item) => {
    dispatch(alterarFiltros({ tipoEvento: item ? [item.id] : [] }));
  };

  const columns = useMemo(
    () => [
      {
        field: 'dataHoraInicio',
        headerName: 'Horário de execução',
        flex: 1,
        minWidth: 180,
        valueFormatter: (value) => formatDateTime(value)
      },
      {
        field: 'servico',
        headerName: 'Serviço',
        flex: 1.4,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ overflow: 'hidden' }}
          >
            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
              {buildServicoLabel(params.row)}
            </Typography>
            {(isEncryptedFormat(params.row.descricaoRequestBodyFormat) ||
              isEncryptedFormat(params.row.descricaoResponseBodyFormat)) && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  color: theme.palette.warning.main,
                  fontSize: 12
                }}
              >
                <LockOutlined />
              </Box>
            )}
          </Stack>
        )
      },
      {
        field: 'flagSuccesso',
        headerName: 'Situação',
        width: 140,
        renderCell: (params) => (
          <LogSituacaoBadge
            flagSuccesso={params.row.flagSuccesso}
            codigoStatusCode={params.row.codigoStatusCode}
          />
        )
      },
      {
        field: 'numeroDuracaoMs',
        headerName: 'Duração',
        width: 110,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) => formatDuracao(value)
      }
    ],
    [theme.palette.warning.main]
  );

  const sortModel = useMemo(
    () => [{ field: sort.field, sort: sort.direction }],
    [sort]
  );

  const paginationModel = useMemo(
    () => ({ page: lista.page - 1, pageSize: lista.pageSize }),
    [lista.page, lista.pageSize]
  );

  return (
    <Dialog
      open={modalAberto}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none'
        }
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box>
          <Typography variant="h2">Logs de Integração</Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Auditoria das chamadas vinculadas à simulação selecionada
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="Fechar"
          size="small"
          sx={{ color: theme.palette.text.secondary }}
        >
          <CloseOutlined />
        </IconButton>
      </Box>

      <Divider />

      <Box sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
        <Stack spacing={2}>
          <FormCard title={'FILTROS'} border>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="dataInicioFrom">De</InputLabel>
                  <OutlinedInput
                    id="dataInicioFrom"
                    name="dataInicioFrom"
                    placeholder="DD/MM/AAAA"
                    fullWidth
                    formProps={formProps}
                    InputProps={{ inputComponent: DateTextField }}
                    onChange={onChangeData('dataInicioFrom')}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="dataInicioTo">Até</InputLabel>
                  <OutlinedInput
                    id="dataInicioTo"
                    name="dataInicioTo"
                    placeholder="DD/MM/AAAA"
                    fullWidth
                    formProps={formProps}
                    InputProps={{ inputComponent: DateTextField }}
                    onChange={onChangeData('dataInicioTo')}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="situacao">Situação</InputLabel>
                  <Autocomplete
                    id="situacao"
                    name="situacao"
                    placeholder="Todas"
                    fullWidth
                    formProps={formProps}
                    labelField="nome"
                    idField="id"
                    domain={SITUACAO_DOMAIN}
                    onChange={onChangeSituacao}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="codigoFluxo">Serviço</InputLabel>
                  <Autocomplete
                    id="codigoFluxo"
                    name="codigoFluxo"
                    placeholder="Todos"
                    fullWidth
                    formProps={formProps}
                    labelField="nome"
                    idField="id"
                    domain={FLUXO_DOMAIN}
                    onChange={onChangeFluxo}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <Stack spacing={0}>
                  <InputLabel htmlFor="tipoEvento">Origem</InputLabel>
                  <Autocomplete
                    id="tipoEvento"
                    name="tipoEvento"
                    placeholder="Todas"
                    fullWidth
                    formProps={formProps}
                    labelField="nome"
                    idField="id"
                    domain={TIPO_EVENTO_DOMAIN}
                    onChange={onChangeTipoEvento}
                  />
                </Stack>
              </Grid>
            </Grid>
          </FormCard>

          <FormCard
            title={'CHAMADAS REGISTRADAS'}
            border
            subtitle={
              lista.total > 0
                ? `${lista.total} chamada${lista.total > 1 ? 's' : ''} encontrada${
                    lista.total > 1 ? 's' : ''
                  }`
                : 'Nenhuma chamada encontrada'
            }
            contentSX={{ p: 0 }}
          >
            <DataGrid
              rows={lista.data}
              columns={columns}
              getRowId={(row) => row.idLogAuditoria}
              loading={lista.loading}
              pageSize={lista.pageSize}
              pageSizeOptions={[10, 25, 50]}
              noLineText="Nenhuma chamada encontrada para os filtros."
              onRowClick={(params) =>
                dispatch(selecionarLog(params.row.idLogAuditoria))
              }
              paginationMode="server"
              sortingMode="server"
              filterMode="server"
              rowCount={lista.total}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) =>
                dispatch(
                  alterarPaginacao({
                    page: model.page + 1,
                    pageSize: model.pageSize
                  })
                )
              }
              sortModel={sortModel}
              onSortModelChange={(model) => {
                const item = model?.[0];
                if (item) {
                  dispatch(
                    alterarSort({
                      field: item.field,
                      direction: item.sort || 'desc'
                    })
                  );
                }
              }}
              initialState={{ density: 'compact' }}
            />
          </FormCard>

          <LogDetalhe
            data={detalhe.data}
            loading={detalhe.loading}
            idSelecionado={detalhe.idSelecionado}
          />
        </Stack>
      </Box>

      <Divider />

      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor: theme.palette.background.paper
        }}
      >
        <Button onClick={handleClose} variant="outlined" color="secondary">
          Fechar
        </Button>
      </Box>
    </Dialog>
  );
};

export default LogsIntegracaoModal;
